import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  FitnessCenter as FitnessCenterIcon,
  LocalDining as LocalDiningIcon,
  DirectionsRun as DirectionsRunIcon,
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ClientData from "../../../../../data/ClientData";
import Mighty45MobileStoreLinks, {
  MIGHTY45_IOS_APP_STORE_URL,
  MIGHTY45_ANDROID_PLAY_STORE_URL,
} from "../../../../../components/Mighty45MobileStoreLinks";
import ClientConnectCodeCard from "./ClientConnectCodeCard";
import { getCardioSessionsPerWeek } from "../../../../../utils/cardioGoalDisplay";
import useWaterUnit from "../../../../../hooks/useWaterUnit";

const HeaderCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor:
    status === "active"
      ? theme.palette.success.main
      : status === "inactive"
        ? theme.palette.warning.main
        : theme.palette.error.main,
  color: "white",
  fontWeight: "bold",
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const GoalsPopButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const GoalSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  minWidth: 300,
  maxWidth: 450,
}));

const ClientHeader = ({ client, onEditClick }) => {
  const { waterLabel } = useWaterUnit();
  const navigate = useNavigate();
  const [connectMenuAnchor, setConnectMenuAnchor] = React.useState(null);
  const [goalsAnchorEl, setGoalsAnchorEl] = React.useState(null);
  const [connectCodeModalOpen, setConnectCodeModalOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCopied = (text) => {
    setSnackbar({
      open: true,
      message: `Copied ${text} to clipboard`,
      severity: "success",
    });
  };

  const handleResetConnectCode = async () => {
    await ClientData.resetConnectCode(client.id);
    setSnackbar({
      open: true,
      message: "Connect code has been reset",
      severity: "info",
    });
    setConnectMenuAnchor(null);
  };

  // Find active nutrition and cardio goals
  const activeNutritionGoal =
    client?.nutritionGoals?.find((goal) => goal.active) || {};
  const activeCardioGoal =
    client?.cardioGoals?.find((goal) => goal.active) || {};

  const isRegistered = Boolean(client?.authId);
  const coachContactDisplay =
    (client?.coachContactEmail && String(client.coachContactEmail).trim()) ||
    (client?.email && String(client.email).trim()) ||
    "";
  const signInEmailDisplay =
    (client?.authLoginEmail && String(client.authLoginEmail).trim()) ||
    (isRegistered
      ? (client?.email && String(client.email).trim()) || ""
      : "");

  if (!client) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
        p={4}
      >
        <Typography variant="h6">Loading client information...</Typography>
      </Box>
    );
  }

  return (
    <>
      <HeaderCard>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar src={client.avatarUrl} sx={{ width: 80, height: 80 }}>
                {client.firstName?.[0]}
                {client.lastName?.[0]}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Stack spacing={1.5}>
                <Typography variant="h4" fontWeight="bold">
                  {client.firstName} {client.lastName}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <StatusChip
                    status={client.status || "active"}
                    label={client.status || "Active"}
                    size="small"
                  />
                  <Stack spacing={0.25} alignItems="flex-start">
                    <Tooltip title="Email you keep for your own records (invites, notes). Edit under Edit client → Basic Info.">
                      <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary" component="span">
                          Contact email:
                        </Typography>
                        <Typography variant="body2" component="span">
                          {coachContactDisplay || "—"}
                        </Typography>
                      </Stack>
                    </Tooltip>
                    <Tooltip title="Email the client uses to sign in to the Mighty45 app (Firebase account). Share this if they forgot which address they registered with.">
                      <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary" component="span">
                          Sign-in email (account):
                        </Typography>
                        <Typography variant="body2" component="span">
                          {!isRegistered
                            ? "Not registered yet"
                            : signInEmailDisplay || "—"}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                  {client?.statistics?.sex && (
                    <Typography variant="body2" color="text.secondary">
                      {client.statistics.sex === "male" ? "Male" : "Female"}
                      {client.statistics.birthDate &&
                        `, ${moment().diff(
                          moment(client.statistics.birthDate.toDate()),
                          "years",
                        )} years`}
                    </Typography>
                  )}
                  {client?.statistics?.experience && (
                    <Chip
                      size="small"
                      label={
                        client.statistics.experience.charAt(0).toUpperCase() +
                        client.statistics.experience.slice(1)
                      }
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Progress over time">
                  <IconButton
                    onClick={() => {
                      if (client?.uid && client?.organizationId) {
                        navigate(
                          `/home/clients/progress/${client.uid}/${client.organizationId}`,
                        );
                      }
                    }}
                    sx={{
                      color: "secondary.main",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ShowChartIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Client">
                  <IconButton
                    onClick={onEditClick}
                    sx={{
                      color: "primary.main",
                      bgcolor: "primary.light + '0A'",
                      "&:hover": {
                        bgcolor: "primary.light",
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Quick Stats with Connect Code */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Tooltip
                    title="Uses the most recent weight from the client profile or latest check-in (currentWeight or weight)."
                    arrow
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Latest weight
                    </Typography>
                  </Tooltip>
                  <Typography variant="h6" fontWeight="bold">
                    {(() => {
                      const currentWeight =
                        client.statistics?.currentWeight ??
                        client.statistics?.weight;
                      const weightValue = Number(currentWeight);
                      return !isNaN(weightValue) && weightValue > 0
                        ? `${Math.ceil(weightValue)} lbs`
                        : "Not Set";
                    })()}
                  </Typography>
                  {(() => {
                    const currentWeight =
                      client.statistics?.currentWeight ??
                      client.statistics?.weight;
                    const startingWeight = client.statistics?.startingWeight;
                    const currentWeightValue = Number(currentWeight);
                    const startingWeightValue = Number(startingWeight);

                    if (
                      !isNaN(startingWeightValue) &&
                      startingWeightValue > 0 &&
                      !isNaN(currentWeightValue) &&
                      currentWeightValue > 0
                    ) {
                      const weightDiff =
                        startingWeightValue - currentWeightValue;
                      return (
                        <Stack spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            Starting weight: {Math.ceil(startingWeightValue)} lbs
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              weightDiff > 0
                                ? "success.main"
                                : weightDiff < 0
                                  ? "error.main"
                                  : "text.secondary"
                            }
                            sx={{ fontWeight: weightDiff !== 0 ? 600 : 400 }}
                          >
                            {weightDiff > 0
                              ? `↓ ${Math.ceil(weightDiff)} lbs lost`
                              : weightDiff < 0
                                ? `↑ ${Math.ceil(Math.abs(weightDiff))} lbs gained`
                                : "No change"}
                          </Typography>
                        </Stack>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Height
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {client.statistics?.height || "Not Set"}
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Goal
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.2,
                      height: "2.4em", // Fixed height for 2 lines
                    }}
                    title={client.statistics?.goal || "Not Set"}
                  >
                    {client.statistics?.goal || "Not Set"}
                  </Typography>
                  <GoalsPopButton
                    size="small"
                    endIcon={<InfoIcon fontSize="small" />}
                    onClick={(e) => setGoalsAnchorEl(e.currentTarget)}
                  >
                    View Goals
                  </GoalsPopButton>
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard>
                <CardContent sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Check-in Day
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {client.checkInDay || "Not Set"}
                  </Typography>
                </CardContent>
              </MetricCard>
            </Grid>

            {/* Connect Code Metric Card */}
            {client.connectCode && (
              <Grid item xs={6} sm={4} md>
                <MetricCard sx={{ position: "relative" }}>
                  <CardContent sx={{ p: 2, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Connect Code
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {client.connectCode}
                      </Typography>
                      <Tooltip title="Copy connect code">
                        <span>
                          <CopyToClipboard
                            text={client.connectCode}
                            onCopy={() => handleCopied("code")}
                          >
                            <IconButton size="small" aria-label="Copy connect code">
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </CopyToClipboard>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => setConnectMenuAnchor(e.currentTarget)}
                        sx={{ ml: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      startIcon={<HelpOutlineIcon />}
                      onClick={() => setConnectCodeModalOpen(true)}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        p: 0.5,
                      }}
                    >
                      How to invite a client
                    </Button>
                    <Mighty45MobileStoreLinks
                      compact
                      sx={{
                        mt: 1.25,
                        textAlign: "center",
                        justifyContent: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    />
                  </CardContent>
                </MetricCard>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </HeaderCard>

      {/* Connect Code Menu */}
      <Menu
        anchorEl={connectMenuAnchor}
        open={Boolean(connectMenuAnchor)}
        onClose={() => setConnectMenuAnchor(null)}
      >
        <MenuItem
          dense
          onClick={() => {
            window.open(
              MIGHTY45_IOS_APP_STORE_URL,
              "_blank",
              "noopener,noreferrer",
            );
            setConnectMenuAnchor(null);
          }}
        >
          <ListItemText primary="Open App Store listing" />
        </MenuItem>

        <MenuItem
          dense
          onClick={() => {
            window.open(
              MIGHTY45_ANDROID_PLAY_STORE_URL,
              "_blank",
              "noopener,noreferrer",
            );
            setConnectMenuAnchor(null);
          }}
        >
          <ListItemText primary="Open Google Play listing" />
        </MenuItem>

        <MenuItem onClick={handleResetConnectCode}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reset Connect Code</ListItemText>
        </MenuItem>
      </Menu>

      {/* Detailed Goals Popover */}
      <Popover
        open={Boolean(goalsAnchorEl)}
        anchorEl={goalsAnchorEl}
        onClose={() => setGoalsAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <GoalSection>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Client Goals
          </Typography>

          {/* Goal Statement */}
          {client.statistics?.goal && client.statistics.goal !== "Not Set" && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Goal Statement:
              </Typography>
              <Typography variant="body2">
                {client.statistics.goal}
              </Typography>
            </Box>
          )}

          {/* Nutrition Goals */}
          {activeNutritionGoal?.id && (
            <>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <LocalDiningIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="medium">
                  Nutrition Plan: {activeNutritionGoal.name}
                </Typography>
              </Stack>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Proteins
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeNutritionGoal.proteins}g
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Carbs
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeNutritionGoal.carbs}g
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Fats
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeNutritionGoal.fats}g
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Water
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {waterLabel(activeNutritionGoal.water)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Fiber
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {Number(activeNutritionGoal.fiber || 0).toFixed(1)}g
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Cardio Goals */}
          {activeCardioGoal?.id && (
            <>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <DirectionsRunIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="medium">
                  Cardio: {activeCardioGoal.name}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeCardioGoal.duration}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Sessions
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {getCardioSessionsPerWeek(activeCardioGoal) ?? "—"}/week
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Intensity
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {activeCardioGoal.intensity}
                  </Typography>
                </Grid>
                {activeCardioGoal.targetHR && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Target Heart Rate
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {activeCardioGoal.targetHR} BPM
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Supplements Section */}
          {client?.supplementPrescriptions?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <FitnessCenterIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="medium">
                  Supplements
                </Typography>
              </Stack>
              {client.supplementPrescriptions
                .filter((s) => s.isActive)
                .map((supplement) => (
                  <Box key={supplement.id} sx={{ mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {supplement.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {supplement.dosage}, {supplement.frequency}
                    </Typography>
                  </Box>
                ))}
            </>
          )}

          {!activeNutritionGoal?.id &&
            !activeCardioGoal?.id &&
            client?.supplementPrescriptions?.length === 0 && (
              <Typography variant="body1" color="text.secondary">
                No active goals set for this client
              </Typography>
            )}
        </GoalSection>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Connect Code Details Modal */}
      <Dialog
        open={connectCodeModalOpen}
        onClose={() => setConnectCodeModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Client Connect Code
          </Typography>
        </DialogTitle>
        <DialogContent>
          <ClientConnectCodeCard client={client} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConnectCodeModalOpen(false)}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientHeader;
